import FormHeader from "@/components/FormHeader";
import SkeletonRow from "@/components/SkeletonRow";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/axios";
import { getListJurusanSekolah } from "@/services/getData";
import { BentukPendidikan, JurusanSekolah, SysProgramStudi } from "@/types";
import { AxiosError } from "axios";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import toast from "react-hot-toast";
import { FaMap } from "react-icons/fa6";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

type InputJurusan = {
  id_ref_jurusan_sekolah: number;
};
type InputState = {
  id_sys_program_studi: string;
  list_jurusan_sekolah: InputJurusan[];
};

const patchData = async (newData: InputState) => {
  const response = await api.patch(
    "/setting/sys-prodi-jurusan-sekolah/update-multiple",
    newData,
  );

  return response.data;
};

export default function FormJurusanSekolah({
  selectedRow,
  closeModal,
}: {
  selectedRow: SysProgramStudi | null;
  closeModal: () => void;
}) {
  const { data: listJurusanSekolah, isPending: isPendingListJurusanSekolah } =
    useQuery({
      queryKey: ["listJurusanSekolah"],
      queryFn: () => getListJurusanSekolah(),
    });

  const [searchQuery, setSearchQuery] = useState("");
  const [form, setForm] = useState<InputState>({
    id_sys_program_studi: selectedRow?.id_sys_program_studi || "",
    list_jurusan_sekolah:
      selectedRow?.sys_prodi_jurusan_sekolah?.map((jurusan) => {
        return {
          id_ref_jurusan_sekolah: Number(jurusan?.id_ref_jurusan_sekolah),
        };
      }) || [],
  });

  const filteredListJurusan = Array.isArray(listJurusanSekolah)
    ? listJurusanSekolah.filter((item: JurusanSekolah) =>
        item?.nama_jurusan_sekolah
          ?.toLowerCase()
          .startsWith(searchQuery.toLowerCase()),
      )
    : [];

  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: patchData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["periode"] });
      queryClient.invalidateQueries({ queryKey: ["listSysJurusanSekolah"] });
      closeModal();
      toast.success(data?.message || "Success.");
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>;
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred.",
      );
    },
  });

  const isAllChecked =
    filteredListJurusan?.length > 0 &&
    filteredListJurusan.every((item) =>
      form.list_jurusan_sekolah.some(
        (jurusan) =>
          jurusan.id_ref_jurusan_sekolah === item.id_ref_jurusan_sekolah,
      ),
    );

  const toggleCheckAll = (checked: boolean) => {
    setForm((prev) => ({
      ...prev,
      list_jurusan_sekolah: checked
        ? filteredListJurusan.map((item) => ({
            id_ref_jurusan_sekolah: item.id_ref_jurusan_sekolah,
          }))
        : [],
    }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    mutate(form);
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
      <FormHeader
        icon={<FaMap />}
        title={`Mapping Data Jurusan Sekolah pada prodi ${selectedRow?.unitkerja?.nama}`}
      />
      <div>
        <div className="mb-5">
          <Label>Cari Jurusan Sekolah</Label>
          <Input
            placeholder="Search..."
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
          />
        </div>
        <div className="mb-5 flex items-center gap-3">
          <Checkbox checked={isAllChecked} onCheckedChange={toggleCheckAll} />
          <Label>{isAllChecked ? "Uncheck" : "Check"} All</Label>
        </div>
        {isPendingListJurusanSekolah ? (
          <div className="flex flex-col gap-y-3">
            {Array(10)
              .fill(null)
              .map((_, index) => {
                return <SkeletonRow key={index} />;
              })}
          </div>
        ) : (
          <section className="grid grid-cols-2 gap-4">
            {filteredListJurusan?.map((item: JurusanSekolah) => {
              const isChecked = form.list_jurusan_sekolah.some(
                (jurusan) =>
                  jurusan.id_ref_jurusan_sekolah ===
                  item.id_ref_jurusan_sekolah,
              );

              return (
                <div
                  key={item.id_ref_jurusan_sekolah}
                  className="flex items-center gap-3"
                >
                  <Checkbox
                    checked={isChecked}
                    onCheckedChange={(checked: boolean) => {
                      setForm((prev) => {
                        const updatedList = checked
                          ? [
                              ...prev.list_jurusan_sekolah,
                              {
                                id_ref_jurusan_sekolah:
                                  item.id_ref_jurusan_sekolah,
                              },
                            ]
                          : prev.list_jurusan_sekolah.filter(
                              (jurusan) =>
                                jurusan.id_ref_jurusan_sekolah !==
                                item.id_ref_jurusan_sekolah,
                            );
                        return { ...prev, list_jurusan_sekolah: updatedList };
                      });
                    }}
                  />
                  <div className="flex gap-3 items-center">
                    <Label>{item.nama_jurusan_sekolah}</Label>
                    <div className="flex gap-1">
                      {item?.bentuk_pendidikan?.map(
                        (value: BentukPendidikan) => (
                          <Badge variant={"secondary"}>{value}</Badge>
                        ),
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </section>
        )}
      </div>
      <div className="mt-3 flex gap-3">
        <Button
          type="button"
          variant={"outline"}
          onClick={closeModal}
          disabled={isPending}
        >
          Batal
        </Button>
        <Button className="flex items-center gap-3" disabled={isPending}>
          {isPending ? (
            <>
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              Processing...
            </>
          ) : (
            "Simpan"
          )}
        </Button>
      </div>
    </form>
  );
}
